
interface RenderByStatus {
  ok: () => React.ReactNode;
  pending?: () => React.ReactNode;
  error?: () => React.ReactNode;
}

interface RenderByData<T> {
  data: (data: T) => React.ReactNode;
  none?: (err?: Error) => React.ReactNode;
}

const isRenderByStatus = (x: any): x is RenderByStatus => x.ok != null;

/**
 * Wrap StateStatus handling into one expression for simpler JSX. state.data may be null!
 * @param status the StateStatus
 * @param elements the elements for each state
 */
export function MatchStateStatus(status: StateStatus, elements: RenderByStatus): React.ReactNode {
  switch (status) {
    case "OK":
      return elements.ok();
    case "PENDING":
      return elements.pending ? elements.pending() : null;
    case "ERROR":
      return elements.error ? elements.error() : null;
    default:
      return null;
  }
}

/**
 * Wrap StateStatus handling into one expression for simpler JSX.
 * Pass in a set of render functions for either
 * 1. the state's status (ok, pending, error), or
 * 2. whether the state has data (data, none).
 * @param status the StateStatus
 * @param renderCollection the elements for each state
 */
export function MatchState<T>(state: IState<T>, renderCollection: RenderByStatus | RenderByData<T>): React.ReactNode {
  if (isRenderByStatus(renderCollection)) {
    if (!state) {
      return null;
    }

    switch (state.status) {
      case "OK":
        return state.data ? renderCollection.ok() : null;
      case "PENDING":
        return renderCollection.pending ? renderCollection.pending() : null;
      case "ERROR":
        return renderCollection.error ? renderCollection.error() : null;
      default:
        return null;
    }
  }
  else {
    // Sometimes it's useful to show data that's in the store even if an ERROR action is dispatched.
    if (state && state.data != null) {
      return renderCollection.data(state.data);
    }
    else {
      return renderCollection.none ? renderCollection.none(state ? state.error : null) : null;
    }
  }
}

export const combineStateStatuses = (...statuses: StateStatus[]): StateStatus => {
  if (statuses.some(s => s === "PENDING")) {
    return "PENDING";
  }
  else if (statuses.some(s => s === "ERROR")) {
    return "ERROR";
  }
  else {
    return "OK";
  }
};
